import React, { useState, useEffect } from 'react';
import { useCollection } from '../../contexts/CollectionContext.tsx';
import CollectionDescriptionEditor from './CollectionDescriptionEditor.tsx';
import CollectionColorEditor from './CollectionColorEditor.tsx';
import './EditContent.css';

interface CollectionColors {
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  accentColor: string;
  h1Color: string;
}

const EditContent: React.FC = () => {
  const { selectedCollection, loading: collectionsLoading, userCollections } = useCollection();
  const [description, setDescription] = useState<string>('');
  const [colors, setColors] = useState<CollectionColors | undefined>(undefined);
  const [fetchingDescription, setFetchingDescription] = useState(false);
  const [fetchingColors, setFetchingColors] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  // Fetch description and colors whenever selectedCollection changes
  useEffect(() => {
    if (!selectedCollection) return;

    const fetchCollectionData = async () => {
      setFetchingDescription(true);
      setFetchingColors(true);
      
      try {
        const response = await fetch(
          `https://${process.env.REACT_APP_SHOP_DOMAIN}/api/2024-01/graphql.json`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': process.env.REACT_APP_STOREFRONT_ACCESS_TOKEN!,
            },
            body: JSON.stringify({
              query: `
                query getCollection($handle: String!) {
                  collectionByHandle(handle: $handle) {
                    description
                    metafield_background: metafield(namespace: "custom", key: "background_color") {
                      value
                    }
                    metafield_text: metafield(namespace: "custom", key: "text_color") {
                      value
                    }
                    metafield_button: metafield(namespace: "custom", key: "button_color") {
                      value
                    }
                    metafield_accent: metafield(namespace: "custom", key: "accent_color") {
                      value
                    }
                    metafield_h1: metafield(namespace: "custom", key: "h1_color") {
                      value
                    }
                  }
                }
              `,
              variables: { handle: selectedCollection }
            })
          }
        );
        
        const data = await response.json();
        const collection = data.data?.collectionByHandle;
        
        setDescription(collection?.description || '');
        
        // Set colors if they exist
        if (collection?.metafield_background?.value) {
          setColors({
            backgroundColor: collection.metafield_background.value,
            textColor: collection.metafield_text?.value || '#FFFFFF',
            buttonColor: collection.metafield_button?.value || '#4CAF50',
            accentColor: collection.metafield_accent?.value || '#3fb8cc',
            h1Color: collection.metafield_h1?.value || '#FFFFFF'
          });
        } else {
          setColors(undefined); // No colors set yet
        }
      } catch (error) {
        console.error('Error fetching collection data:', error);
      } finally {
        setFetchingDescription(false);
        setFetchingColors(false);
      }
    };

    fetchCollectionData();
  }, [selectedCollection]);

  const handleUpdate = () => {
    setPreviewKey(prev => prev + 1);
  };

  if (collectionsLoading) {
    return <div className="loading">Loading your collections...</div>;
  }

  if (userCollections.length === 0) {
    return (
      <div className="no-collections">
        <p>You don't have any collections assigned to you yet.</p>
      </div>
    );
  }

  return (
    <div className="edit-content-container">
      <div className="edit-content-body">
        {/* Left side: Editor */}
        <div className="editor-panel">
          <h2>Edit Details</h2>
          
          {fetchingDescription ? (
            <div className="loading">Loading description...</div>
          ) : (
            <CollectionDescriptionEditor
              collectionHandle={selectedCollection!}
              initialDescription={description}
              onUpdate={handleUpdate}
            />
          )}

          {fetchingColors ? (
            <div className="loading">Loading colors...</div>
          ) : (
            <CollectionColorEditor
              collectionHandle={selectedCollection!}
              initialColors={colors}
              onUpdate={handleUpdate}
            />
          )}

          <div className="editor-section coming-soon">
            <h3>Banner Image</h3>
            <p>Coming soon: Upload and manage your collection banner</p>
          </div>
        </div>

        {/* Right side: Live Preview */}
        <div className="preview-panel">
          <h2>Live Preview</h2>
          <div className="preview-frame-container">
            <iframe
              key={previewKey}
              src={`https://${process.env.REACT_APP_SHOP_DOMAIN}/collections/${selectedCollection}`}
              className="preview-iframe"
              title="Collection Preview"
            />
          </div>
          <div className="preview-actions">
            <button 
              onClick={() => setPreviewKey(prev => prev + 1)}
              className="refresh-preview-btn"
            >
              🔄 Refresh Preview
            </button>
            <a 
              href={`https://${process.env.REACT_APP_SHOP_DOMAIN}/collections/${selectedCollection}`}
              target="_blank"
              rel="noopener noreferrer"
              className="view-live-btn"
            >
              View Live Page ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditContent;